// Fleet Reports API (license-gated Fleet KPI dashboard, 2026-07-14).
import { createApi } from "@reduxjs/toolkit/query/react";
import baseQueryWithReauth from "./requestInterceptor";

// NOTE — deliberately NO createApi-level refetchOnMountOrArgChange (unlike
// services/analytics.js): /fleet-report is a full raw-telemetry scan on the
// backend, and that flag would re-issue it on every sub-tab mount. A re-Load
// with unchanged args is done via the hook's refetch() from the Load button.
export const reportingApi = createApi({
  reducerPath: "reportingApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["FleetConfig", "FleetReport"],
  endpoints: (builder) => ({
    // Capability + page bootstrap. Token-only on the backend; returns
    // { payload: { enabled, timezone, vehicles, metrics, granularities, ... } }
    // — enabled:false (HTTP 200) for tenants without the feature.
    getFleetConfig: builder.query({
      query: ({ token }) => ({
        url: `aggregation/fleet-config`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": token,
        },
      }),
      keepUnusedDataFor: 900,
      providesTags: ["FleetConfig"],
    }),
    // The report itself. startDate/endDate are inclusive YYYY-MM-DD in the
    // tenant's timezone; everything else (serviceId, sensorIds) is derived
    // server-side from the license. 403 = capability off → the page renders
    // its permission/empty state, never a generic error toast.
    getFleetReport: builder.query({
      query: ({ token, startDate, endDate }) => ({
        url: `aggregation/fleet-report?startDate=${startDate}&endDate=${endDate}`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": token,
        },
      }),
      keepUnusedDataFor: 900,
      providesTags: ["FleetReport"],
    }),
    // Dashboard-editable settings (2026-07-23): fuel price / currency / vehicle
    // de-selection. Invalidates ONLY the config: settings apply on the next
    // report Load (the backend keys the report cache on these settings, so that
    // Load can never serve a stale pre-edit report). Deliberately NOT
    // invalidating FleetReport - that would auto-refire a guaranteed-cache-miss
    // full raw scan on every Save and burn the tenant's 10/min report budget.
    updateFleetSettings: builder.mutation({
      query: ({ token, body }) => ({
        url: `aggregation/fleet-settings`,
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": token,
        },
        body,
      }),
      invalidatesTags: ["FleetConfig"],
    }),
  }),
});

export const { useGetFleetConfigQuery, useGetFleetReportQuery, useUpdateFleetSettingsMutation } = reportingApi;
